import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import ViewerApp from "../ViewerApp"; // Fix import path to main App
import Dashboard from "./pages/Dashboard"; // Fix import path to Dashboard
import Entities from "./pages/Entities";
import Rules from "./pages/Rules";
import FilterTabs from "./pages/FilterTabs";
import Thresholds from "./pages/Thresholds";
import AiUsesList from "./pages/AiUsesList";
import LoginHistoryList from "./pages/LoginHistoryList";
import AdminLayout from "./layout/AdminlLayout";
import { APP_BASE_URL } from "../config/api";
import { logOut } from "../features/auth/authSlice";
import { clearAccountData } from "../features/account/accountSlice";
import useActivityStatus from "../hooks/useActivityStatus";

const ACTIVITY_TIMEOUT_ENV = process.env.REACT_APP_ACTIVITY_TIMEOUT || 120;
const ACTIVITY_TIMEOUT = parseInt(ACTIVITY_TIMEOUT_ENV, 10);

/* SET THE ROUTES */
export default function AdminApp() {
  // Rename component to avoid naming conflict
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, token, isAuthenticated } = useSelector((state) => state.auth);
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const isActive = useActivityStatus(ACTIVITY_TIMEOUT);

  // Inactivity timeout effect
  useEffect(() => {
    if (isActive === false) {
      console.warn("⏱️ Admin panel inactivity timeout - logging out");
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      dispatch(clearAccountData());
      dispatch(logOut());
      navigate("/", { replace: true });
    }
  }, [isActive, dispatch, navigate]);

  useEffect(() => {
    const checkAdminAccess = async () => {
      // If not authenticated, redirect
      if (!isAuthenticated || !token) {
        setIsAuthorized(false);
        setIsChecking(false);
        return;
      }

      // If user is already in Redux and has admin role, allow access
      if (user && user.role === "admin") {
        setIsAuthorized(true);
        setIsChecking(false);
        return;
      }

      // Verify token and check role from backend
      try {
        const response = await fetch(
          `${APP_BASE_URL}/api/verify/token/${token}`,
        );
        if (!response.ok) {
          setIsAuthorized(false);
          setIsChecking(false);
          return;
        }

        const data = await response.json();
        if (data.verify && data.user && data.user.role === "admin") {
          setIsAuthorized(true);
        } else {
          setIsAuthorized(false);
        }
      } catch (error) {
        console.error("Error verifying admin access:", error);
        setIsAuthorized(false);
      } finally {
        setIsChecking(false);
      }
    };

    checkAdminAccess();
  }, [isAuthenticated, token, user]);

  // Show loading while checking
  if (isChecking) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying access...</p>
        </div>
      </div>
    );
  }

  // If not authorized, redirect to landing page
  if (!isAuthorized) {
    return <Navigate to="/" replace />;
  }

  // If authorized, render admin routes
  return (
    <AdminLayout>
      <Routes>
        {/* Admin area */}
        <Route path="/admin" element={<Dashboard />} />
        <Route path="/entities" element={<Entities />} />
        <Route path="/rules" element={<Rules />} />
        <Route path="/thresholds" element={<Thresholds />} />
        <Route path="/filter" element={<FilterTabs />} />
        <Route path="/ai-uses" element={<AiUsesList />} />
        <Route path="/login-history" element={<LoginHistoryList />} />

        {/* Fallback to viewer app for everything else
                 NEED TO CAHNGE IT . NEED TO MOVE THE APP SOMEWHERE*/}
        <Route path="/" element={<ViewerApp />} />
      </Routes>
    </AdminLayout>
  );
}
